"use client";

import React from "react";
import {Box, Rating, Stack, Typography} from "@mui/material";
import Image from "next/image";
import {FeefoReview} from "@/types/dto/destinations-page/destination-page-types";
import ColletteReadMoreToggle from "@/components/common-controls/collette-read-more-toggle";
import {useTranslations} from "next-intl";
import { formatDate } from "@/utils/date-utils";

interface ReviewCardProps {
  review: FeefoReview;
  index: string;
  isProductClickable?: boolean;
}

const FeefoReviewCard = ({
  review,
  index,
  isProductClickable = true
}: ReviewCardProps) => {
  const t = useTranslations("DESTINATIONS");
  return (
    <Stack
      sx={{
        display: "flex",
        padding: "3.2rem",
        flexDirection: "column",
        alignItems: "flex-start",
        justifyContent: "space-between",
        gap: "4rem",
        width: "100%",
        minHeight: "35rem",
        borderRadius: "2rem",
        backgroundColor: "background.default",
        boxShadow: 6
      }}
      component="article"
    >
      <Stack
        sx={{
          width: "100%",
          gap: "1.4rem"
        }}
        data-testid={`feefo-review_${index}_content`}
      >
        <Box
          sx={{
            display: "flex",
            width: "100%",
            flexDirection: "row",
            justifyContent: "space-between",
            alignItems: "flex-start"
          }}
          data-testid={`feefo-review_${index}_content-header`}
        >
          <Image
            src="https://i.gocollette.com/img/icons/quotes_blue.svg"
            alt="quotes"
            height={65}
            width={65}
            style={{
              padding: "1.5rem 1rem"
            }}
            data-testid={`feefo-review_${index}_quote-icon`}
          />
          <Stack gap="0.4rem" alignItems="flex-end">
            <Typography
              sx={{
                fontSize: "1.2rem"
              }}
              data-testid={`feefo-review_${index}_tour-rating-label`}
            >
              {t("Tour Rating")}:
            </Typography>
            <Rating
              defaultValue={review.reviewRating}
              precision={1}
              readOnly
              size="small"
              data-testid={`feefo-review_${index}_star-rating`}
            />
          </Stack>
        </Box>
        <ColletteReadMoreToggle
          description={review.review}
          linesShown={3}
          testId={`feefo-review_${index}_review`}
          collapseOffScreen
          textComponent="blockquote"
        />
      </Stack>
      <Stack
        sx={{
          display: "flex",
          flexDirection: "column",
          "& > :first-child": { marginBottom: "1.2rem" }
        }}
      >
        <Typography
          variant="h5"
          component="p"
          data-testid={`feefo-review_${index}_author`}
          sx={{
              color: "text.primary",
              fontSize:{xs:"1.4rem", sm:"1.6rem"},
              fontWeight: 700
          }}
        >
          {review.reviewAuthor.length > 0 ? review.reviewAuthor : t("Trusted Customer")}
        </Typography>
        <Typography
          variant="subtitle_lg"
          href={isProductClickable ? review.tourUrl : undefined}
          component={isProductClickable ? "a" : "p"}
          color="secondary"
          sx={{
            fontWeight: 500,
            fontSize:{ xs:"1.4rem", sm:"1.6rem"},
            textDecoration: "underline !important",
            ...(!isProductClickable ? { color: "text.primary" } : {})
          }}
          data-testid={`feefo-review_${index}_tour-name`}
        >
          {review.tourName}
        </Typography>
        <Typography
          variant="body2"
          component="p"
          sx={{ color: "text.secondary", fontWeight: 500 }}
          data-testid={`feefo-review_${index}_review-date`}
        >
          {formatDate(review.reviewDate, { month: "short", day: "numeric", year: "numeric" })}
        </Typography>
      </Stack>
    </Stack>
  );
};

export default FeefoReviewCard;